import { useMemo, useCallback, memo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { List } from 'react-window';
import { WorkOrder, api } from '../../lib/api';
import { STATUS_STAGES, getStatusBadgeVariant } from './StatusWorkflow';
import { Badge } from '../ui/Badge';

// =============================================================================
// TYPES
// =============================================================================

interface KanbanViewProps {
  workorders: WorkOrder[];
  onSelectWorkorder: (workorder: WorkOrder) => void;
}

interface KanbanColumnProps {
  stage: typeof STATUS_STAGES[number];
  workorders: WorkOrder[];
  onSelectWorkorder: (workorder: WorkOrder) => void;
}

interface KanbanCardProps {
  workorder: WorkOrder;
  onClick: () => void;
}

// =============================================================================
// STAGE COLORS
// =============================================================================

const STAGE_COLORS: Record<number, { bg: string; border: string; header: string }> = {
  0: { bg: 'bg-amber-50', border: 'border-amber-200', header: 'bg-amber-100 text-amber-800' },
  1: { bg: 'bg-blue-50', border: 'border-blue-200', header: 'bg-blue-100 text-blue-800' },
  2: { bg: 'bg-purple-50', border: 'border-purple-200', header: 'bg-purple-100 text-purple-800' },
  3: { bg: 'bg-indigo-50', border: 'border-indigo-200', header: 'bg-indigo-100 text-indigo-800' },
  4: { bg: 'bg-green-50', border: 'border-green-200', header: 'bg-green-100 text-green-800' },
  5: { bg: 'bg-forest-50', border: 'border-forest-200', header: 'bg-forest-100 text-forest-800' },
  6: { bg: 'bg-red-50', border: 'border-red-200', header: 'bg-red-100 text-red-800' },
};

// =============================================================================
// KANBAN CARD
// =============================================================================

const KanbanCard = memo(({ workorder, onClick }: KanbanCardProps) => {
  const isUrgent = workorder.urgent;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-xl bg-white border shadow-sm hover:shadow-md transition-shadow ${
        isUrgent ? 'border-earth-300 ring-1 ring-earth-200' : 'border-sage-200'
      }`}
    >
      {/* Urgent indicator */}
      {isUrgent && (
        <div className="flex items-center gap-1 mb-2">
          <span className="text-earth-500 text-sm font-medium">! Urgent</span>
        </div>
      )}

      {/* Client name */}
      <div className="font-medium text-forest-900 text-sm truncate">
        {workorder.clientName}
      </div>

      {/* Item description */}
      <div className="text-sage-600 text-xs mt-1 truncate">
        {workorder.qty > 1 ? `${workorder.qty}x ` : ''}{workorder.itemName}
      </div>

      {/* Location if present */}
      {workorder.location && (
        <div className="text-sage-500 text-xs mt-1 truncate">
          {workorder.location}
        </div>
      )}

      {/* Bottom row: status badge and age */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-sage-100">
        <Badge variant={getStatusBadgeVariant(workorder.status)} size="sm">
          {workorder.status.replace(/^\d+\.\s*/, '')}
        </Badge>
        {workorder.ageDays !== undefined && workorder.ageDays > 0 && (
          <span className={`text-xs ${workorder.ageDays > 7 ? 'text-earth-600 font-medium' : 'text-sage-500'}`}>
            {workorder.ageDays}d
          </span>
        )}
      </div>
    </button>
  );
});

KanbanCard.displayName = 'KanbanCard';

// =============================================================================
// KANBAN COLUMN
// =============================================================================

interface KanbanColumnExtendedProps extends KanbanColumnProps {
  onPrefetch?: (id: number) => void;
}

const KanbanColumn = memo(({ stage, workorders, onSelectWorkorder, onPrefetch }: KanbanColumnExtendedProps) => {
  const colors = STAGE_COLORS[stage.stage] || STAGE_COLORS[0];
  const count = workorders.length;

  // Stable callback factory to avoid re-creating onClick for each card
  const handleCardClick = useCallback((wo: WorkOrder) => {
    onSelectWorkorder(wo);
  }, [onSelectWorkorder]);

  // Row renderer for virtualized list
  const Row = useCallback(({ index, style }: { index: number; style: React.CSSProperties }) => {
    const wo = workorders[index];
    return (
      <div
        style={style}
        className="px-2"
        onMouseEnter={() => onPrefetch?.(wo.id)}
        onFocus={() => onPrefetch?.(wo.id)}
      >
        <KanbanCard
          workorder={wo}
          onClick={() => handleCardClick(wo)}
        />
      </div>
    );
  }, [workorders, handleCardClick, onPrefetch]);

  return (
    <div className={`flex flex-col min-w-[280px] max-w-[320px] rounded-xl border ${colors.border} ${colors.bg}`}>
      {/* Column header */}
      <div className={`px-4 py-3 rounded-t-xl ${colors.header}`}>
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold text-sm">
            {stage.label}
          </h3>
          <span className="px-2 py-0.5 rounded-full bg-white/50 text-xs font-medium">
            {count}
          </span>
        </div>
      </div>

      {/* Cards container - virtualized for performance */}
      <div className="flex-1 p-2">
        {workorders.length === 0 ? (
          <div className="text-center py-8 text-sage-400 text-sm">
            No items
          </div>
        ) : (
          <List<Record<string, never>>
            defaultHeight={window.innerHeight - 280}
            rowComponent={Row}
            rowProps={{} as Record<string, never>}
            rowCount={workorders.length}
            rowHeight={138}  // Card height (130px) + gap (8px)
            className="scrollbar-thin scrollbar-thumb-sage-300 scrollbar-track-sage-100"
          />
        )}
      </div>
    </div>
  );
});

KanbanColumn.displayName = 'KanbanColumn';

// =============================================================================
// KANBAN VIEW
// =============================================================================

export function KanbanView({ workorders, onSelectWorkorder }: KanbanViewProps) {
  const queryClient = useQueryClient();

  // Prefetch work order detail on hover
  const prefetchWorkOrder = useCallback((id: number) => {
    queryClient.prefetchQuery({
      queryKey: ['workorder', id],
      queryFn: () => api.workorders.getById(id),
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });
  }, [queryClient]);

  // Group workorders by stage
  const groupedByStage = useMemo(() => {
    const groups = new Map<number, WorkOrder[]>();

    // Initialize all stages
    STATUS_STAGES.forEach(stage => {
      groups.set(stage.stage, []);
    });

    // Group workorders
    workorders.forEach(wo => {
      const stage = STATUS_STAGES.find(s => s.statuses.includes(wo.status));
      if (stage) {
        groups.get(stage.stage)?.push(wo);
      } else {
        // Default to stage 0 if unknown status
        groups.get(0)?.push(wo);
      }
    });

    // Sort workorders within each stage by urgency then age
    groups.forEach((wos) => {
      wos.sort((a, b) => {
        // Urgent first
        if (a.urgent && !b.urgent) return -1;
        if (!a.urgent && b.urgent) return 1;
        // Then by age (oldest first)
        return (b.ageDays || 0) - (a.ageDays || 0);
      });
    });

    return groups;
  }, [workorders]);

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 px-4">
      {STATUS_STAGES.map((stage) => (
        <KanbanColumn
          key={stage.stage}
          stage={stage}
          workorders={groupedByStage.get(stage.stage) || []}
          onSelectWorkorder={onSelectWorkorder}
          onPrefetch={prefetchWorkOrder}
        />
      ))}
    </div>
  );
}
